// Authoritative content for AutoDrive Tech Solutions.
// Derived from the project brief. Update contact details / assets when real ones are supplied.

import {
  ShieldCheck, ClipboardCheck, FlaskConical, Scale, FileText, Cpu,
  Gauge, Compass, ShieldAlert, LifeBuoy, Rocket, Users,
  Zap, Bike, Car, Truck, Bus, Tractor, Package, HardHat, CarFront, Milestone,
} from "lucide-react";

export const COMPANY = {
  name: "AutoDrive Tech Solutions",
  shortName: "AutoDrive",
  domain: "autodrivetechsolutions.com",
  tagline: "Automotive Certification & Regulatory Consulting",
  email: "info@autodrivetechsolutions.com",
  phone: "+91-9760849094",
  address: "AutoDrive Tech Solutions, Manesar, Gurugram, Haryana, 122051",
  linkedin: "https://www.linkedin.com/company/autodrive-tech-solutions",
};

export const IMAGES = {
  logo: "https://customer-assets-v7afamib.emergentagent.net/job_autodrive-certified/artifacts/wkwqk8km_logo_generated.png",
  hero: "https://customer-assets-v7afamib.emergentagent.net/job_autodrive-certified/artifacts/bpbay0vp_Hero_image.png",
  about: "https://customer-assets-v7afamib.emergentagent.net/job_autodrive-certified/artifacts/nf4u2hbv_imagerd_services_home.png",
  services: "https://images.unsplash.com/photo-1581092583537-20d51b4b4f1b",
  corporate: "https://images.pexels.com/photos/37320179/pexels-photo-37320179.jpeg",
  collaborate: "https://customer-assets-v7afamib.emergentagent.net/job_autodrive-certified/artifacts/oyto9p6t_image2_aboutautidrivetechsolution.png",
  teamA: "https://customer-assets-v7afamib.emergentagent.net/job_autodrive-certified/artifacts/4mibre6s_abhishek_image.png",
  teamB: "https://customer-assets-v7afamib.emergentagent.net/job_autodrive-certified/artifacts/umwibeeu_navjot%20image.png",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services", dropdown: "services" },
  { label: "Industries", to: "/industries", dropdown: "industries" },
  { label: "Our Team", to: "/team" },
];

export const SERVICES = [
  {
    slug: "type-approval-certification",
    icon: ShieldCheck,
    title: "Type Approval Certification (TAC)",
    short: "End-to-end management of type approval to bring compliant vehicles to market.",
    description:
      "We manage the complete Type Approval Certification lifecycle — from applicability mapping against CMVR and AIS standards to final certificate issuance. Our team coordinates with testing agencies, prepares technical files and ensures your vehicle or component meets every mandatory Indian regulatory requirement before market entry.",
    benefits: [
      "Complete applicability and regulation mapping",
      "Reduced approval timelines through expert coordination",
      "Single point of accountability across agencies",
      "Fewer rejections with right-first-time documentation",
    ],
    process: ["Applicability Study", "Documentation", "Testing Coordination", "Agency Submission", "Certificate Issuance"],
  },
  {
    slug: "conformity-of-production",
    icon: ClipboardCheck,
    title: "Conformity of Production (COP)",
    short: "Sustained compliance so series production matches your approved type.",
    description:
      "Conformity of Production ensures that vehicles rolling off your line remain identical to the approved prototype. We establish COP frameworks, prepare quality documentation, and support periodic audits so your certification stays valid throughout the production run.",
    benefits: [
      "Robust COP quality frameworks",
      "Audit-ready documentation and records",
      "Continuous certification validity",
      "Reduced risk of production non-conformity",
    ],
    process: ["Gap Assessment", "Framework Setup", "Documentation", "Audit Support", "Ongoing Monitoring"],
  },
  {
    slug: "regulatory-consulting",
    icon: Scale,
    title: "Regulatory Consulting",
    short: "Clear, expert interpretation of India's evolving automotive regulations.",
    description:
      "Indian automotive regulation is complex and continuously evolving. Our regulatory consultants translate CMVR, AIS and BIS requirements into practical, actionable roadmaps — helping you make confident product and compliance decisions well ahead of deadlines.",
    benefits: [
      "Plain-language regulatory interpretation",
      "Proactive tracking of regulatory changes",
      "Compliance roadmaps aligned to your product plan",
      "Strategic advisory for new product programs",
    ],
    process: ["Consultation", "Regulation Mapping", "Impact Analysis", "Roadmap", "Advisory"],
  },
  {
    slug: "testing-management",
    icon: FlaskConical,
    title: "Testing Management",
    short: "Planned, coordinated and witnessed testing across accredited agencies.",
    description:
      "We plan and manage your entire test program across accredited agencies — scheduling, sample coordination, witnessing and report management. Our involvement minimises repeat tests, controls costs and keeps your certification timeline on track.",
    benefits: [
      "Optimised test planning and scheduling",
      "Coordination with ARAI, ICAT, NATRAX and more",
      "Fewer repeat tests and lower cost",
      "Consolidated reporting and traceability",
    ],
    process: ["Test Planning", "Sample Coordination", "Scheduling", "Witnessing", "Report Management"],
  },
  {
    slug: "documentation-submission",
    icon: FileText,
    title: "Documentation & Submission",
    short: "Precise technical files prepared and submitted right the first time.",
    description:
      "Accurate documentation is the backbone of successful certification. We prepare complete technical files, information documents and submission packages to regulatory authority standards — eliminating the back-and-forth that delays approvals.",
    benefits: [
      "Authority-grade technical documentation",
      "Complete, right-first-time submissions",
      "Fewer queries and resubmissions",
      "Organised records for future extensions",
    ],
    process: ["Data Collection", "Technical File Prep", "Review", "Submission", "Query Resolution"],
  },
  {
    slug: "component-system-certification",
    icon: Cpu,
    title: "Component & System Certification",
    short: "Certification for components, sub-systems and safety-critical parts.",
    description:
      "From lighting and braking to EV batteries and electronic systems, we certify individual components and sub-systems against applicable AIS and international standards — enabling suppliers and OEMs to supply into regulated markets with confidence.",
    benefits: [
      "Component-level applicability expertise",
      "EV, electronic and safety-system coverage",
      "Supplier and OEM certification support",
      "International standard alignment",
    ],
    process: ["Scope Definition", "Standard Mapping", "Testing", "Submission", "Certification"],
  },
];

export const ABOUT_FEATURES = [
  { icon: ShieldCheck, title: "Specialized Certification", text: "Focused exclusively on automotive certification and homologation consulting." },
  { icon: LifeBuoy, title: "End-to-End Support", text: "From applicability study through to certificate — a single accountable partner." },
  { icon: Rocket, title: "Faster Market Entry", text: "Expert coordination compresses approval timelines and accelerates launch." },
  { icon: Users, title: "Trusted Expertise", text: "Ex-agency professionals who understand the process from the inside." },
];

export const PROCESS_STEPS = [
  { icon: Compass, title: "Consultation", text: "Understand your product, market and certification goals." },
  { icon: ClipboardCheck, title: "Requirement Analysis", text: "Map applicable CMVR, AIS and BIS regulations." },
  { icon: FileText, title: "Documentation", text: "Prepare authority-grade technical files." },
  { icon: FlaskConical, title: "Testing Coordination", text: "Plan and witness tests at accredited agencies." },
  { icon: Scale, title: "Regulatory Liaison", text: "Manage submissions and resolve authority queries." },
  { icon: ShieldCheck, title: "Certification", text: "Secure your Type Approval / COP certificate." },
  { icon: Rocket, title: "Market Launch", text: "Enter the market compliant and on schedule." },
];

export const AGENCIES = [
  { abbr: "ARAI", name: "Automotive Research Association of India", text: "India's premier automotive R&D and testing agency for type approval, homologation and certification." },
  { abbr: "ICAT", name: "International Centre for Automotive Technology", text: "A leading NATRiP centre for testing, certification and homologation of vehicles and components." },
  { abbr: "NATRAX", name: "National Automotive Test Tracks", text: "One of the world's largest proving grounds for vehicle dynamics, performance and durability testing." },
  { abbr: "GARC", name: "Global Automotive Research Centre", text: "Advanced testing and certification facility supporting emissions, safety and component validation." },
  { abbr: "CIRT", name: "Central Institute of Road Transport", text: "Testing and certification authority for commercial vehicles, buses and public transport applications." },
  { abbr: "BIS", name: "Bureau of Indian Standards", text: "National standards body governing product certification and mandatory conformity requirements." },
];

export const WHY_CHOOSE = [
  { icon: Rocket, title: "Faster Certification", text: "Expert coordination and right-first-time files cut weeks from your approval timeline." },
  { icon: Compass, title: "Regulatory Clarity", text: "We translate complex CMVR, AIS and BIS requirements into clear, actionable steps." },
  { icon: ShieldAlert, title: "Risk Mitigation", text: "Anticipate compliance gaps early and avoid costly rejections and rework." },
  { icon: LifeBuoy, title: "End-to-End Support", text: "One accountable partner from applicability study to final certificate." },
  { icon: Gauge, title: "Faster Time-to-Market", text: "Streamlined testing and submissions get compliant products to market sooner." },
  { icon: Users, title: "Experienced Experts", text: "Former agency professionals who understand certification from the inside." },
];

export const INDUSTRIES = [
  { icon: Zap, name: "Electric Vehicles", text: "Type approval and battery, powertrain and safety-system certification for EVs." },
  { icon: Bike, name: "Two Wheelers", text: "Certification and homologation for motorcycles, scooters and mopeds." },
  { icon: CarFront, name: "Three Wheelers", text: "L5 and passenger/goods three-wheeler type approval, including e-rickshaws." },
  { icon: Car, name: "Passenger Vehicles", text: "Homologation and compliance for cars and passenger transport vehicles." },
  { icon: Truck, name: "Commercial Vehicles", text: "Regulatory approval for goods carriers and commercial platforms." },
  { icon: Bus, name: "Bus", text: "Certification for buses and public transport vehicle bodies." },
  { icon: Truck, name: "Truck", text: "Type approval for trucks, tractors and heavy goods vehicles." },
  { icon: Package, name: "Trailer", text: "Certification for trailers, semi-trailers and towed equipment." },
  { icon: HardHat, name: "Construction Equipment", text: "Compliance for construction and earth-moving equipment." },
  { icon: Tractor, name: "Agricultural Vehicles", text: "Type approval for tractors and agricultural machinery." },
];

export const ACHIEVEMENTS = [
  { value: 100, suffix: "+", label: "Type Approval Certificates for E-Rickshaw" },
  { value: 35, suffix: "+", label: "Type Approval Certificates for 3W L5 Auto" },
  { value: 25, suffix: "+", label: "Type Approval Certificates for 2 Wheeler" },
  { value: 20, suffix: "+", label: "Type Approval Certificates for Bus, Truck & Trailer" },
  { value: 100, suffix: "+", label: "Extension Certificates for Electric 2W & 3W Vehicles" },
];

export const CLIENT_LOGOS = [
  "MotorWorks", "VoltEV", "TransAxle", "RoadMaster", "UrbanMobility",
  "GreenDrive", "AutoNova", "MetroCoach", "TorqueTech", "PrimeMotors",
];

export const EXPERTS = [
  {
    slug: "abhishek-yadav",
    name: "Abhishek Yadav",
    role: "Homologation Expert – Automotive Compliance & Certification",
    shortRole: "Homologation Expert",
    former: "Former ICAT Professional",
    association: "International Centre for Automotive Technology (ICAT), Government Of India",
    experience: "5 Years",
    image: IMAGES.teamA,
    overview: [
      "Abhishek Yadav is an accomplished Homologation Expert with five years of comprehensive experience at ICAT, specializing in vehicle type approval, certification, and regulatory compliance across the automotive sector. His technical expertise spans a wide range of vehicle categories including Electric Two-Wheelers, Electric Three-Wheelers, Buses, Trucks, Trailers, Ambulances, Construction Equipment Vehicles (CEV), and Tractors.",
      "Throughout his tenure, Abhishek has been instrumental in managing end-to-end homologation processes — from regulatory documentation and testing coordination to final certification and liaison with statutory authorities. He brings in-depth understanding of CMVR, AIS, and BIS standards, ensuring full compliance with national automotive regulations.",
    ],
    competencies: [
      "Vehicle Type Approval & Certification (EV and ICE platforms)",
      "Regulatory Compliance with ICAT, ARAI, GARC, CIRT, BIS",
      "CMVR & AIS Standards Implementation",
      "Technical Documentation and Test Planning",
      "Vehicle System Evaluation & Component Validation",
      "Cross-functional Coordination with OEMs and Testing Agencies",
    ],
    attributes: [
      "Strong regulatory and technical acumen within the automotive domain",
      "Proven ability to manage multiple homologation programs effectively",
      "Committed to delivering precision, compliance, and efficiency",
      "Excellent stakeholder communication and documentation skills",
    ],
  },
  {
    slug: "navjot-singh",
    name: "Navjot Singh",
    role: "Homologation Expert – Electric Vehicle Certification & Regulatory Affairs",
    shortRole: "EV Certification Expert",
    former: "Former ICAT Professional",
    association: "International Centre for Automotive Technology (ICAT), Government Of India",
    experience: "4 Years",
    image: IMAGES.teamB,
    overview: [
      "Navjot Singh is an experienced Homologation Expert with five years of specialized experience at ICAT, focusing on electric vehicle type approval, regulatory compliance, and certification. His domain expertise covers Electric Two-Wheelers, Low-Speed Electric Two-Wheelers, E-Rickshaws, and E-Cart Three-Wheelers, with a strong emphasis on projects under the FAME II (Faster Adoption and Manufacturing of Hybrid & Electric Vehicles) Subsidy Scheme.",
      "Navjot has successfully managed multiple homologation programs, ensuring complete compliance with CMVR, AIS, and BIS standards. His work involved extensive coordination with OEMs, component suppliers, and regulatory authorities to secure approvals and subsidies under national EV incentive frameworks.",
    ],
    competencies: [
      "EV Type Approval & Certification (2W, Low-Speed 2W, E-Rickshaw, E-Cart 3W)",
      "FAME II Subsidy Process Management & Documentation",
      "CMVR & AIS Standards Compliance",
      "Regulatory Liaison with ICAT, ARAI, GARC, CIRT, and BIS",
      "Technical Evaluation of Electric Powertrains, Battery Systems & Controllers",
      "Homologation Testing Coordination & Certification Reporting",
    ],
    attributes: [
      "Deep understanding of EV regulations, homologation norms, and certification pathways",
      "Strong technical and regulatory documentation expertise",
      "Proven experience in managing end-to-end subsidy compliance under FAME II",
      "Detail-driven, proactive, and efficient in stakeholder communication",
    ],
  },
];

export const VALUES = [
  { icon: ShieldCheck, title: "Integrity", text: "We uphold the highest standards of engineering honesty and regulatory rigour." },
  { icon: Gauge, title: "Precision", text: "Accuracy in documentation and testing is non-negotiable in certification." },
  { icon: LifeBuoy, title: "Partnership", text: "We work as an extension of your team, accountable from start to finish." },
  { icon: Rocket, title: "Momentum", text: "We keep programs moving — compliant and on schedule." },
];

export const TIMELINE = [
  { year: "Foundation", title: "Built on Agency Experience", text: "Founded by former testing-agency professionals to give manufacturers an insider advantage in certification." },
  { year: "Focus", title: "Specialisation in Homologation", text: "Established a dedicated homologation and type approval practice across vehicle categories." },
  { year: "Growth", title: "Expanding into EV & Components", text: "Extended expertise into electric vehicles, battery systems and component certification." },
  { year: "Today", title: "Trusted Compliance Partner", text: "A go-to regulatory consulting partner for India's automotive manufacturers and suppliers." },
];

export const VEHICLE_CATEGORIES = [
  "Electric Vehicles", "Two Wheelers", "Three Wheelers", "Passenger Vehicles",
  "Commercial Vehicles", "Bus", "Truck", "Trailer", "Construction Equipment", "Agricultural Vehicles", "Other",
];

export const SERVICE_OPTIONS = [
  "Type Approval Certification (TAC)",
  "Conformity of Production (COP)",
  "Regulatory Consulting",
  "Testing Management",
  "Documentation & Submission",
  "Component & System Certification",
  "Other",
];

export const CONTACT_METHODS = ["Email", "Phone", "WhatsApp"];

export const MILESTONE_ICON = Milestone;
